/* eslint-disable complexity */
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { Consolidation } from '@futrli-sdk/consolidations';
import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { DATASOURCES_STATUS } from '@futrli/shared-services/app-core/constants';
import { Integration } from '@futrli-sdk/app-store';
import { isPureSFAWorkspace } from '@futrli/shared-services/workspaces/utils/utils';
import { OrganisationListItem, WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { OrganisationDSDisplayStatus } from '../types';

export interface ConsolidationActions {
  delete: boolean;
  edit: boolean;
}

export interface ExtendedConsolidation extends Consolidation {
  actions: ConsolidationActions;
}

export interface OrganisationListItemActions {
  delete: boolean;
  reconnect: boolean;
  removeAccess: boolean;
  resubscribe: boolean;
  updateValues: boolean;
}

export interface ExtendedOrganisationListItem extends OrganisationListItem {
  actions: OrganisationListItemActions;
  datasourceDisplayStatus: OrganisationDSDisplayStatus | null;
  integration?: Integration;
  flags: ReturnType<typeof getOrgFlags>;
}

export interface ExtendedWorkspaceListItem extends WorkspacesListingItem {
  actions: {
    setAsDefault: boolean;
    updateName: boolean;
  };
  consolidations: ExtendedConsolidation[];
  flags: ReturnType<typeof getWorkspaceFlags>;
  organisations: ExtendedOrganisationListItem[];
}

const RECONNECT_DS_SLUG_BLACKLIST = ['sage50', 'sage-active', 'excel', 'sfa'];

const DS_RECONNECT_STATUSES = [
  DATASOURCES_STATUS.sync_failure,
  DATASOURCES_STATUS.sync_initial_failure,
  DATASOURCES_STATUS.connect_start,
  DATASOURCES_STATUS.never_connected,
  DATASOURCES_STATUS.connect_failure,
  DATASOURCES_STATUS.connect_timeout,
  DATASOURCES_STATUS.disconnect_success
];

/**
 * Returns permissions for the workspace, based on its properties and the current user.
 * @param workspace - The workspace to get the flags for
 * @param currentUser - The current user
 */
export const getWorkspacePermissions = (workspace: WorkspacesListingItem, currentUser: CurrentUserCheck) => ({
  hasUpdateOrgsPermission: !!workspace.permissions.find(p => p.slug === 'organisations'),
  isWorkspaceGuest: [workspace.member?.role?.name, workspace.member?.role?.reference].some(
    r => r?.toLowerCase() === 'guest'
  ),
  isWorkspaceOwner: workspace.owner?.id === currentUser.id,
  isWorkspaceSuperAdmin: !!workspace.member?.role?.super_admin
});

/**
 * Returns flags for the workspace, based on its properties and the current user.
 * @param workspace - The workspace to get the flags for
 * @param currentUser - The current user
 */
export const getWorkspaceFlags = (workspace: WorkspacesListingItem, currentUser: CurrentUserCheck) => ({
  ...getWorkspacePermissions(workspace, currentUser),
  isPureSFA: isPureSFAWorkspace(workspace)
});

/**
 * Returns flags for the organisation, based on its properties and the workspace it belongs to.
 * @param workspace - The workspace the organisation belongs to
 * @param org - The organisation to get the flags for
 * @param integration - The integration for the organisation's datasource, if any
 */
const getOrgFlags = (
  workspace: WorkspacesListingItem,
  org: OrganisationListItem,
  integration: Integration | undefined
) => ({
  isActiveOrg: org.billed_by_status === 'active',
  isExcelOrg: integration?.slug === 'excel',
  isSage50Org: integration?.slug === 'sage50',
  isSFAOrg: integration?.slug === 'sfa'
});

/**
 * Gets the available actions for this organisation
 * @param workspace - The workspace the organisation belongs to
 * @param org - The organisation to get the actions for
 * @param integration - The integration for the organisation's datasource, if any
 * @param currentUser - The current user
 * @param flags - The LaunchDarkly flags
 */
const getOrgActions = (
  workspace: WorkspacesListingItem,
  org: OrganisationListItem,
  integration: Integration | undefined,
  currentUser: CurrentUserCheck,
  flags: LDFlagSet = {}
): OrganisationListItemActions => {
  const datasourceSyncStatus = (org.datasource?.sync_status || '') as DATASOURCES_STATUS;
  const { hasUpdateOrgsPermission, isWorkspaceOwner } = getWorkspacePermissions(workspace, currentUser);
  const { isActiveOrg, isExcelOrg, isSage50Org, isSFAOrg } = getOrgFlags(workspace, org, integration);

  const reconnect =
    !!integration &&
    !RECONNECT_DS_SLUG_BLACKLIST.includes(integration.slug) &&
    DS_RECONNECT_STATUSES.includes(datasourceSyncStatus);

  return {
    delete: hasUpdateOrgsPermission && !isSage50Org && !isSFAOrg,
    reconnect,
    removeAccess: !!flags.platformRemoveOwnAccessFromOrganisation && !isWorkspaceOwner,
    resubscribe: isSFAOrg && org.billed_by_status !== 'active' && org.billed_by_status !== 'pending',
    updateValues: isActiveOrg && isExcelOrg
  };
};

/**
 * Gets the available actions for a consolidation.
 * @param workspace - The workspace the consolidation belongs to
 */
const getConsolidationActions = (
  workspace: WorkspacesListingItem,
  currentUser: CurrentUserCheck
): ConsolidationActions => {
  const { hasUpdateOrgsPermission } = getWorkspacePermissions(workspace, currentUser);

  return {
    delete: hasUpdateOrgsPermission,
    edit: hasUpdateOrgsPermission
  };
};

/**
 * Gets the display status for the organisation's datasource, based on its sync status and other properties.
 * @param org - The organisation to get the display status for
 */
const getDatasourceDisplayStatus = (org: OrganisationListItem) => {
  const datasourceSyncStatus = (org.datasource?.sync_status || '') as DATASOURCES_STATUS;
  const isActiveOrg = org.billed_by_status === 'active';
  const isHoldingOrg = !!org.is_holding;

  if (isHoldingOrg || !isActiveOrg) {
    return null;
  } else if (
    datasourceSyncStatus === DATASOURCES_STATUS.never_connected ||
    datasourceSyncStatus === DATASOURCES_STATUS.connect_failure ||
    datasourceSyncStatus === DATASOURCES_STATUS.connect_timeout ||
    datasourceSyncStatus === DATASOURCES_STATUS.disconnect_success
  ) {
    return OrganisationDSDisplayStatus.NOT_CONNECTED;
  } else if (datasourceSyncStatus === DATASOURCES_STATUS.connect_start) {
    return OrganisationDSDisplayStatus.CONNECTING;
  } else if (
    datasourceSyncStatus === DATASOURCES_STATUS.sync_initial_start ||
    datasourceSyncStatus === DATASOURCES_STATUS.sync_start ||
    datasourceSyncStatus === DATASOURCES_STATUS.sync_initial_update_status ||
    datasourceSyncStatus === DATASOURCES_STATUS.sync_update_status
  ) {
    return OrganisationDSDisplayStatus.SYNCING;
  } else if (datasourceSyncStatus === DATASOURCES_STATUS.pending_setup) {
    return OrganisationDSDisplayStatus.PENDING_SETUP;
  } else if (
    datasourceSyncStatus === DATASOURCES_STATUS.sync_failure ||
    datasourceSyncStatus === DATASOURCES_STATUS.sync_initial_failure
  ) {
    return OrganisationDSDisplayStatus.SYNC_ERROR;
  } else {
    return OrganisationDSDisplayStatus.SYNCED;
  }
};

/**
 * Transforms a workspace into an extended workspace with additional properties for each organisation.
 * @param workspace - The workspace to transform
 * @param integrations - The list of integrations
 * @param currentUser - The current user
 * @param consolidations - The list of consolidations
 * @param flags - The LaunchDarkly flags
 * @returns The extended workspace
 */
export const processWorkspace = (
  workspace: WorkspacesListingItem,
  integrations: Integration[],
  currentUser: CurrentUserCheck,
  consolidations: Consolidation[] = [],
  LDFlags: LDFlagSet = {}
): ExtendedWorkspaceListItem => {
  const workspaceFlags = getWorkspaceFlags(workspace, currentUser);

  return {
    ...workspace,
    actions: {
      setAsDefault: !!LDFlags?.defaultWorkspace,
      updateName: workspaceFlags.isWorkspaceOwner || workspaceFlags.isWorkspaceSuperAdmin
    },
    consolidations: consolidations.map(consolidation => ({
      ...consolidation,
      actions: getConsolidationActions(workspace, currentUser)
    })),
    flags: workspaceFlags,
    organisations: workspace.organisations.map(org => {
      const integration = org.datasource?.integration_id
        ? integrations.find(i => i.id === org.datasource?.integration_id)
        : undefined;

      return {
        ...org,
        actions: getOrgActions(workspace, org, integration, currentUser, LDFlags),
        datasourceDisplayStatus: getDatasourceDisplayStatus(org),
        flags: getOrgFlags(workspace, org, integration),
        integration
      };
    })
  };
};

/**
 * Transforms a list of workspaces into extended workspaces with additional properties for each organisation.
 * @param workspaces - The list of workspaces to transform
 * @param integrations - The list of integrations
 * @param currentUser - The current user
 * @param consolidations - The list of consolidations
 * @param flags - The LaunchDarkly flags
 * @returns The list of extended workspaces
 */
export const processWorkspaces = (
  workspaces: WorkspacesListingItem[],
  integrations: Integration[],
  currentUser: CurrentUserCheck,
  consolidations: Consolidation[] = [],
  flags: LDFlagSet = {}
): ExtendedWorkspaceListItem[] =>
  workspaces.map(workspace =>
    processWorkspace(
      workspace,
      integrations,
      currentUser,
      consolidations.filter(consolidation => consolidation.workspace_id === workspace.id),
      flags
    )
  );
